<template>
  <header
    :class="classes"
    :style="{
      ...alertsHeightVar,
      ...headerHeightVar
    }"
    data-testid="g-header"
    role="banner"
    @focusin="handleFocusin"
    @focusout="handleFocusout"
  >
    <div id="skiplink" class="g-header__skip gui-container">
      <a
        href="#content"
        class="g-header__skip-link"
        @click.prevent="skipToContent"
        >{{ skipToContentText }}</a
      >
    </div>

    <AlertsList
      a11y-title="Site wide messages"
      :alerts="alerts"
      @dismiss="dismissMessage"
    />

    <BaseHeader :items="baseNav" :logo-hidden="logoHidden" />
    <div class="g-header__container gui-container">
      <div class="g-header__inner">
        <div class="g-header__nav">
          <nav
            class="g-header__nav-row"
            role="navigation"
            :aria-label="navAriaLabel"
          >
            <ListOfLinks :links="primaryNav" />
          </nav>
        </div>
      </div>
    </div>

    <JPCAMobileNavigation
      :primary-items="primaryNav"
      :base-nav="baseNav"
      :has-search="hasSearch"
    />
  </header>
</template>

<script>
import {
  GuiHeader,
  ListOfLinks,
  AlertsList,
  useLocalization
} from "@thegetty-private/getty-ui";
import JPCAMobileNavigation from "@/components/globals/jpcaMobileNavigation/index.vue";
import BaseHeader from "./baseHeader/index.vue";

export default {
  name: "JPCAHeader",
  components: {
    AlertsList,
    BaseHeader,
    JPCAMobileNavigation,
    ListOfLinks
  },
  extends: GuiHeader,

  props: {
    /**
     * Array of alert objects to display at the top of GuiHeader
     */
    alerts: {
      type: Array,
      default: () => []
    },
    baseUrl: {
      type: String,
      default: "/"
    },
    /**
     * The number of pixels at which to display the header during scrollUp.
     * Leave undefined to display header during scrollUp.
     */
    displayThreshold: {
      type: Number,
      default: undefined
    },
    /**
     * Boolean used to hide or display the logo in the GuiHeader
     */
    forceHideLogo: {
      type: Boolean,
      default: false
    },
    forceOnDark: {
      type: Boolean,
      default: false
    },
    gradient: {
      type: Boolean,
      default: false
    },
    hasSearch: {
      type: Boolean,
      default: false
    },
    /**
     * Array of objects to display as the primary navigation
     */
    primaryNav: {
      type: Array,
      default: null
    },
    /**
     * Array of objects to display as the Base header navigation
     */
    baseNav: {
      type: Array,
      default: null
    },
    sticky: {
      type: Boolean,
      default: false
    },
    /**
     * Style variation
     */
    modifier: {
      type: [String, undefined],
      default: undefined,
      validation: (value) => ["light", undefined].includes(value)
    }
  },
  setup(props) {
    return {
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },
  data() {
    return {
      /**
       * aria-label for navigation list of links
       */
      navAriaLabel: this.translatePart("g-jpca-header.nav_aria_label"),
      /**
       * Skip to Content translated text
       */
      skipToContentText: this.translatePart("header.skip_to_content")
    };
  },
  computed: {
    /**
     * Builds an array of component style class names using a base class name
     * and the Block Element Modifier naming convention for modifiers
     * @return  {Array}  Component style class names
     */
    classes() {
      const baseName = "g-header";
      const classes = [baseName, "g-jpca-header"];

      const addModifier = (modifier) => {
        classes.push(`${baseName}--${modifier}`);
      };

      if (this.forceOnDark || this.isOnDark) addModifier("on-dark");
      if (this.gradient) addModifier("gradient");
      if (this.isAlertsListHidden) addModifier("hide-alerts");
      if (this.isHeaderHidden) addModifier("hide");
      if (this.searchTriggerHover) addModifier("search-hover");
      if (this.showHeaderFill) addModifier("fill");
      if (this.sticky) addModifier("sticky");
      if (this.modifier == "light") addModifier("light");

      return classes;
    }
  },
  methods: {
    /**
     * Scrolls to section with content id (if available)
     */
    skipToContent() {
      const target = document.getElementById("content");
      if (target) {
        // make sure it's focusable so keyboard navigation can continue from there
        // and not back at the top of the page
        target.setAttribute("tabindex", "-1");
        target.focus();
      }
    }
  }
};
</script>

<style lang="scss">
@import "./index.scss";
</style>
